import {
  Card,
  CardContent,
  Divider,
  IconButton,
  Snackbar,
  TextField,
} from '@material-ui/core';

import {
  DevHubButton,
  DevHubModal,
  DevHubTypography,
  DevHubBox,
  DevHubGrid,
  DevHubCard,
} from '@devxdiscover/devhub-ui';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { Fragment, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import ExtensibilityQuery from './ExtensibilityQuery';
import UpdateExtension from './UpdateExtension';
import { Alert } from '@material-ui/lab';
import { Extension } from './types/extension';
import { EditableFields } from './types/editableTextFields';
import { Progress } from '@backstage/core-components';

/**
 * This component is used to display the Plugin Stats section of the Settings page.
 * @constructor
 */
export const TemplateStats = () => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [templates, setTemplates] = useState<Extension[]>([]);
  const [editableFields, setEditableFields] = useState<EditableFields>({});
  const [currentExtensionId, setCurrentExtensionId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<Extension>(
    {} as Extension,
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { data, loading, error } = useQuery(ExtensibilityQuery);
  const [updateExtensionMutation] = useMutation(UpdateExtension);

  useEffect(() => {
    if (!loading && !error && data) {
      const filtered = data?.devhub?.extensions.filter(
        (extension: Extension) => extension.extensionType === 'Template',
      );
      setTemplates(filtered);
    }
  }, [data, loading, error]);

  const handleEdit = (extensionId: string) => {
    const templateIndex = templates.findIndex(
      template => template.id === extensionId,
    );

    if (templateIndex === -1) {
      return;
    }

    setCurrentExtensionId(extensionId);
    // Set the editable fields based on the existing template or default values
    const templateToEdit = templates[templateIndex];
    setEditableFields(prevEditableFields => ({
      ...prevEditableFields,
      [extensionId]: {
        distributionList: templateToEdit.distributionList || '',
        extensionType: templateToEdit.extensionType || '',
        name: templateToEdit.name || '',
        notes: templateToEdit.notes || '',
        owner: templateToEdit.owner || '',
        securityCredentialsUrl: templateToEdit.securityCredentialsUrl || '',
        slackChannel: templateToEdit.slackChannel || '',
      },
    }));
  };

  const handleCancel = () => {
    setEditableFields(prevEditableFields => {
      const updatedFields = { ...prevEditableFields };
      delete updatedFields[currentExtensionId];
      return updatedFields;
    });
    setCurrentExtensionId('');
  };

  const handleOpen = (template: Extension) => {
    setSelectedTemplate(template);
    setModalIsOpen(true);
  };

  const handleClose = () => {
    handleCancel();
    setModalIsOpen(false);
  };

  const handleChange = (templateId: string, field: string, value: string) => {
    setEditableFields(prevEditableFields => ({
      ...prevEditableFields,
      [templateId]: {
        ...prevEditableFields[templateId],
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    if (!currentExtensionId) {
      return;
    }

    const templateIndex = templates.findIndex(
      template => template.id === currentExtensionId,
    );

    if (templateIndex === -1) {
      return;
    }

    const templateToEdit = editableFields[currentExtensionId];

    // Call the Fusion mutation to update the template with the new values
    updateExtensionMutation({
      variables: {
        input: {
          extensionId: currentExtensionId,
          ...templateToEdit,
        },
      },
    }).then(() => {
      setSuccessMessage('Template data successfully saved!');
    });

    setEditableFields(prevEditableFields => {
      const updatedFields = { ...prevEditableFields };
      delete updatedFields[currentExtensionId];
      return updatedFields;
    });

    setCurrentExtensionId('');
    setModalIsOpen(false);
  };

  return (
    <>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      {loading && <Progress />}
      <DevHubGrid container>
        {templates
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(template => (
            <Fragment key={template?.id}>
              <DevHubGrid item xs={12} md={6}>
                <DevHubCard
                  density="compact"
                  title={template?.name}
                  actionItem={
                    <DevHubGrid container margin="xsmall">
                      <DevHubButton
                        buttonColor="primary"
                        variant="outlined"
                        onClick={() => handleOpen(template)}
                      >
                        Open
                      </DevHubButton>
                    </DevHubGrid>
                  }
                  subTitle={template?.owner}
                />
              </DevHubGrid>

              <DevHubModal open={modalIsOpen} onClose={handleClose}>
                <Fragment>
                  <DevHubBox sx={{ display: 'flex', margin: '.5rem' }}>
                    <DevHubTypography variant="h6">
                      {selectedTemplate?.name}
                    </DevHubTypography>
                    {editableFields[selectedTemplate?.id] ? (
                      <>
                        <IconButton onClick={handleSave}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => handleEdit(selectedTemplate?.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </DevHubBox>
                  {editableFields[selectedTemplate?.id] ? (
                    // Show the editable text fields when in edit mode
                    <Card>
                      <CardContent>
                        <TextField
                          label="Name"
                          value={editableFields[selectedTemplate?.id].name}
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'name',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Owner (Github Group)"
                          value={editableFields[selectedTemplate?.id].owner}
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'owner',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Distribution List"
                          value={
                            editableFields[selectedTemplate?.id]
                              .distributionList
                          }
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'distributionList',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Slack Channel"
                          value={
                            editableFields[selectedTemplate?.id].slackChannel
                          }
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'slackChannel',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Notes"
                          value={editableFields[selectedTemplate?.id].notes}
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'notes',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Security Credentials (URL to Vault)"
                          value={
                            editableFields[selectedTemplate?.id]
                              .securityCredentialsUrl
                          }
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'securityCredentialsUrl',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Type (Plugin/Template)"
                          value={
                            editableFields[selectedTemplate?.id].extensionType
                          }
                          onChange={e =>
                            handleChange(
                              selectedTemplate?.id,
                              'extensionType',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                      </CardContent>
                    </Card>
                  ) : (
                    // Show the existing template data in a card when not in edit mode
                    <Card>
                      <CardContent>
                        <DevHubTypography variant="body2">
                          Owner (Github Group):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedTemplate?.owner}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Distribution List:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedTemplate?.distributionList}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Slack Channel:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedTemplate?.slackChannel}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Notes:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedTemplate?.notes}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Security Credentials (URL to Vault):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          <a
                            href={selectedTemplate?.securityCredentialsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {selectedTemplate?.securityCredentialsUrl}
                          </a>
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Type (Plugin/Template):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedTemplate?.extensionType}
                        </DevHubTypography>
                        <Divider />
                      </CardContent>
                    </Card>
                  )}
                </Fragment>
              </DevHubModal>
            </Fragment>
          ))}
      </DevHubGrid>
    </>
  );
};
